import type {
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { isPlainObject } from "lodash";

import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";
import { queryClient } from "@citrine/client/utils";

import { OBJECT_TYPES } from "../data-query";
import { JobExecutionClient } from "../job-execution";
import type { MaterialNode } from "../material-node";
import {
  TERMINAL_CLASS_CRITERIA,
  isClassificationCriterium,
} from "../table-config";

import type {
  GEMDSnapshot,
  GEMDSnapshotCreationJobStatus,
  GEMDSnapshotQuery,
} from "./gemd-snapshot";

interface GEMDSnapshotQueryOptions extends IQuery {
  page?: number;
  per_page?: number;
  root?: string;
  version?: number | "latest";
  archived?: boolean;
  project_id?: string;
}

function isGEMDSnapshot(value: any): value is GEMDSnapshot {
  return (
    isPlainObject(value) &&
    "id" in value &&
    "data" in value &&
    "metadata" in value
  );
}

interface CreateFromGEMDQueryPayload {
  name: string;
  description: string;
  query: GEMDSnapshotQuery;
  rootId?: string;
}

interface JobIdResp {
  job_id: string;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SnapshotJobOutput = {
  snapshotId: string;
};

export class GEMDSnapshotClient extends ClientBase<
  GEMDSnapshot,
  string,
  GEMDSnapshotQueryOptions
> {
  create!: never;
  useCreate!: never;
  update!: never;
  useUpdate!: never;
  delete!: never;
  useDelete!: never;
  jobExecutionClient: JobExecutionClient<SnapshotJobOutput>;

  protected readonly teamId: string;

  protected constructor(teamId: string) {
    super({
      endpoint: `/api/v1/teams/${teamId}/gemd/snapshots`,
      defaultQuery: {
        page: 1,
        per_page: 100,
        root: undefined,
        version: undefined,
        archived: undefined,
        project_id: undefined,
      },
      findField: "response",
    });
    this.teamId = teamId;
    this.jobExecutionClient =
      JobExecutionClient.getInstanceForTeam<SnapshotJobOutput>(teamId);
  }

  static getInstance(teamId: string) {
    return new this(teamId);
  }

  async createAsync(data: CreateFromGEMDQueryPayload, projectId?: string) {
    const { job_id } = await this.request<JobIdResp>({
      method: "POST",
      url: `${this.endpoint}/async`,
      params: projectId ? { project_id: projectId } : undefined,
      data: {
        ...data,
        query: {
          ...data.query,
          object_types: data.query.object_types ?? OBJECT_TYPES,
        },
      },
    });

    const { output } = await this.jobExecutionClient.poll(job_id, {
      interval: 5 * 1000,
      timeout: 60 * 60 * 1000,
    });

    return this.get(output.snapshotId);
  }

  useCreateAsync(
    projectId?: string,
    options: Omit<
      UseMutationOptions<GEMDSnapshot, AxiosError, CreateFromGEMDQueryPayload>,
      "mutationFn"
    > = {},
  ): UseMutationResult<GEMDSnapshot, AxiosError, CreateFromGEMDQueryPayload> {
    return useMutation<GEMDSnapshot, AxiosError, CreateFromGEMDQueryPayload>({
      ...options,
      mutationFn: (data) => this.createAsync(data, projectId),
    });
  }

  getMaterialNodes(id: string) {
    return this.request<MaterialNode[] | null>({
      url: `${this.getUri(id)}/data`,
    });
  }

  useGetMaterialNodes(
    id?: string,
    options: Omit<
      UseQueryOptions<MaterialNode[] | null>,
      "queryKey" | "queryFn"
    > = {},
  ): UseQueryResult<MaterialNode[] | null> {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(id), "material-nodes"],
      queryFn: () => this.getMaterialNodes(id!),
      enabled: Boolean(id && (options.enabled ?? true)),
    });
  }

  async getCreationJobStatus(jobId: string) {
    const result = await this.request<GEMDSnapshotCreationJobStatus>({
      url: `${this.endpoint}/jobs/${jobId}`,
    });
    if (result.status === "SUCCEEDED" && result.snapshot) {
      this.addModelToCache(result.snapshot);
    }
    return result;
  }

  useGetCreationJobStatus(
    jobId?: string,
    options: Omit<
      UseQueryOptions<GEMDSnapshotCreationJobStatus>,
      "queryKey" | "queryFn"
    > = {},
  ): UseQueryResult<GEMDSnapshotCreationJobStatus> {
    return useQuery({
      ...options,
      queryKey: [this.endpoint, "jobs", jobId],
      queryFn: () => this.getCreationJobStatus(jobId!),
      enabled: Boolean(jobId && (options.enabled ?? true)),
    });
  }

  async archiveAllVersions(rootId: string): Promise<void> {
    // The string returned is just the rootId
    await this.request<{ value: string }>({
      method: "PUT",
      url: `${this.endpoint}/roots/${rootId}/archive`,
    });
    this.updateCacheAfterArchiveOrRestore(rootId);
  }

  useArchiveAllVersions(
    options: Omit<
      UseMutationOptions<void, AxiosError, string>,
      "mutationFn"
    > = {},
  ): UseMutationResult<void, AxiosError, string> {
    return useMutation<void, AxiosError, string>({
      ...options,
      mutationFn: (rootId) => this.archiveAllVersions(rootId),
    });
  }

  async restoreAllVersions(rootId: string): Promise<void> {
    // The string returned is just the rootId
    await this.request<{ value: string }>({
      method: "PUT",
      url: `${this.endpoint}/roots/${rootId}/restore`,
    });
    this.updateCacheAfterArchiveOrRestore(rootId);
  }

  useRestoreAllVersions(
    options: Omit<
      UseMutationOptions<void, AxiosError, string>,
      "mutationFn"
    > = {},
  ): UseMutationResult<void, AxiosError, string> {
    return useMutation<void, AxiosError, string>({
      ...options,
      mutationFn: (rootId) => this.restoreAllVersions(rootId),
    });
  }

  // URL to export/download a Gemd Snapshot data to excel
  useGetDownloadDataUrl(
    snapshotId: string,
    options: Omit<UseQueryOptions<string>, "queryKey" | "queryFn"> = {},
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(snapshotId), "download-url"],
      queryFn: () => `${this.endpoint}/${snapshotId}/download`,
      enabled: Boolean(snapshotId) && options.enabled !== false,
    });
  }

  // Separate the endpoint and id into 2 segments so we can do a lookup on the
  // endpoint segment when invalidating entries for archiving/restoring. Ideally
  // we would include the root id in the cache key.
  getModelCacheKey(id?: string): QueryKey {
    return [this.endpoint, id];
  }

  protected isEnabled() {
    return Boolean(this.teamId);
  }

  protected toModel(snapshot: GEMDSnapshot) {
    // Use new material classification criterium for Analysis Workflows that were
    // created with the legacy criterium.  We never supported non-terminal
    // classifications in production so it should be safe to assume that we are
    // only dealing with terminal materials in the legacy classification.
    snapshot.data.query.criteria = snapshot.data.query.criteria.map((c) =>
      isClassificationCriterium(c)
        ? {
            type: "or_operator",
            criteria: [TERMINAL_CLASS_CRITERIA],
          }
        : c,
    );
    return snapshot;
  }

  /**
   * We can't simply invalidate snapshot entries in the cache after mutation
   * because archival happens at the root id level, and the individual snapshot
   * cache keys do not include the root id.
   */
  private updateCacheAfterArchiveOrRestore(rootId: string) {
    // invalidate all find queries
    queryClient.invalidateQueries({ queryKey: this.getQueryCachePrefix() });
    // invalidate individual snapshot entries
    queryClient
      .getQueriesData({ queryKey: [this.endpoint] })
      .forEach(([queryKey, value]) => {
        if (isGEMDSnapshot(value) && value.metadata.root_id === rootId) {
          queryClient.invalidateQueries({ queryKey, exact: true });
        }
      });
  }
}
